import { Box } from '@mui/material';
import { FC, useCallback, useEffect, useReducer, useRef } from 'react';
import shaka from 'shaka-player';
import { Buffering } from './Buffering';
import { Controls } from './Controls';
import { ErrorMessage } from './ErrorMessage';
import { PlayerProvider } from './PlayerContext';
import { Stats } from './Stats';
import { PlayerAction, PlayerState, getKeySystemClearKey, getKeySystemWithUrl, videoReducer } from './Video.helpers';
import { DRM, VideoOptions } from './types';

shaka.polyfill.installAll();

export const Video: FC<VideoOptions> = ({
  source,
  drm = DRM.None,
  licenseUrl,
  certificateUrl,
  clearKey,
  clearKeyId,
  headers,
  autoPlay,
  onReady,
  showStats = false,
  ...args
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<shaka.Player>();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hideTimeout = useRef<NodeJS.Timeout>();

  const [{ playerState, controlsVisible, errorCode }, dispatch] = useReducer(videoReducer, {
    playerState: PlayerState.Created,
    controlsVisible: false,
  });

  const showControls = useCallback(() => {
    clearTimeout(hideTimeout.current);
    dispatch({ type: PlayerAction.ShowControls });
    hideTimeout.current = setTimeout(() => {
      dispatch({ type: PlayerAction.HideControls });
    }, 3000);
  }, []);

  const loadSource = useCallback(
    async (player: shaka.Player) => {
      if (drm === DRM.Widevine || drm === DRM.Fairplay) {
        player.configure(getKeySystemWithUrl(drm, licenseUrl, drm === DRM.Fairplay ? certificateUrl : ''));
      } else if (drm === DRM.Clearkey) {
        player.configure(getKeySystemClearKey(clearKey, clearKeyId || ''));
      }
      if (headers) {
        player.getNetworkingEngine()?.registerRequestFilter((type, request) => {
          if (
            type === shaka.net.NetworkingEngine.RequestType.LICENSE ||
            type === shaka.net.NetworkingEngine.RequestType.MANIFEST ||
            type === shaka.net.NetworkingEngine.RequestType.SEGMENT
          ) {
            for (const property in headers) {
              request.headers[property] = headers[property];
            }
          }
        });
      }
      try {
        await player.load(source);
        dispatch({ type: PlayerAction.Loaded });
        showControls();
      } catch (e) {
        console.error(e);
        const { code } = e as shaka.extern.Error;
        dispatch({ type: PlayerAction.Error, payload: code });
      }
    },
    [drm, headers, licenseUrl, certificateUrl, clearKey, clearKeyId, source, showControls],
  );

  // on startup
  useEffect(() => {
    const createPlayer = async () => {
      if (!playerRef.current && videoRef.current && playerState === PlayerState.Created) {
        const player = new shaka.Player();
        playerRef.current = player;
        await player.attach(videoRef.current);
        dispatch({ type: PlayerAction.Ready });
        onReady && onReady(player);
      }
    };
    createPlayer();
  }, [playerState, onReady]);

  useEffect(() => {
    const player = playerRef.current;
    if (player && playerState === PlayerState.Ready) {
      loadSource(player);
    }
  }, [playerState, loadSource]);

  // Dispose the player after the component is unmounted
  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player) {
        player.unload();
        player.detach();
        player.destroy();
        playerRef.current = undefined;
      }
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
    }
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      position="relative"
      ref={containerRef}
      onMouseLeave={showControls}
      onMouseMove={showControls}
      overflow="hidden"
      maxHeight="calc(100vh - 400px)"
    >
      <video style={{ zIndex: 0, overflow: 'hidden' }} {...args} autoPlay={autoPlay} ref={videoRef} controls={false} />
      {playerState === PlayerState.Error && <ErrorMessage code={errorCode} />}
      {playerState === PlayerState.SourceLoaded && (
        <PlayerProvider player={playerRef.current as shaka.Player}>
          {showStats && <Stats />}
          <Controls show={controlsVisible} onToggleFullscreen={toggleFullscreen} container={containerRef?.current} />
          <Buffering />
        </PlayerProvider>
      )}
    </Box>
  );
};

export default Video;
